import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  HsnExceptionReport,
  HsnExceptionRow,
  HsnMasterStats,
  HsnService,
} from '../hsn.service';

/**
 * HSN Exceptions — every product whose HSN code needs attention.
 *
 * Existing products are deliberately NOT blocked on save: roughly 4.5% of the
 * catalogue carries a malformed code inherited from historical spreadsheet
 * uploads, and refusing those edits would freeze over a thousand products for
 * reasons unrelated to the edit being made. They are surfaced here and fixed
 * deliberately. New products ARE validated at creation, so the backlog cannot
 * grow.
 *
 * Rows are grouped by code, not by product: 158 products sharing one broken
 * code is a single decision.
 *
 * On suggestions — a code that is one digit short may have lost that digit at
 * either end (9109100 → 09109100, but 3304100 → 33041000). The backend checks
 * both against the master and only returns repairs that actually exist, so a
 * "confident" row is a fact rather than a guess. Ambiguous rows need a human.
 */
@Component({
  selector: 'app-hsn-exceptions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hsn-exceptions.component.html',
  styleUrls: ['./hsn-exceptions.component.scss'],
})
export class HsnExceptionsComponent implements OnInit {
  private readonly hsnService = inject(HsnService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly report = signal<HsnExceptionReport | null>(null);
  readonly masterStats = signal<HsnMasterStats | null>(null);

  /** Codes whose product list is expanded. */
  readonly expanded = signal<Set<string>>(new Set());
  /** Per-row manual entry, keyed by the stored code. */
  readonly manualFix: Record<string, string> = {};
  /** Codes currently being written, so the button can disable. */
  readonly applying = signal<Set<string>>(new Set());
  readonly rowMessage = signal<Record<string, string>>({});

  readonly filter = signal<'ALL' | 'STRUCTURE' | 'NOT_IN_MASTER' | 'FIXABLE'>(
    'ALL',
  );
  readonly search = signal('');

  readonly visibleRows = computed<HsnExceptionRow[]>(() => {
    const rows = this.report()?.rows ?? [];
    const f = this.filter();
    const q = this.search().trim().toLowerCase();

    return rows.filter((r) => {
      if (f === 'STRUCTURE' && r.kind !== 'STRUCTURE') return false;
      if (f === 'NOT_IN_MASTER' && r.kind !== 'NOT_IN_MASTER') return false;
      if (f === 'FIXABLE' && !r.confident) return false;
      if (!q) return true;
      return (
        r.hsnCode.toLowerCase().includes(q) ||
        r.sampleProducts.some(
          (p) =>
            p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q),
        )
      );
    });
  });

  /** One-click fixes available — the headline number for the user. */
  readonly confidentCount = computed(
    () => (this.report()?.rows ?? []).filter((r) => r.confident).length,
  );

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    this.hsnService.stats().subscribe({
      next: (s) => this.masterStats.set(s),
      // Stats are decoration; a failure here must not blank the page.
      error: () => this.masterStats.set(null),
    });

    this.hsnService.exceptions().subscribe({
      next: (r) => {
        this.report.set(r);
        this.loading.set(false);
      },
      error: (e) => {
        this.error.set(
          e?.error?.message ?? 'Could not load the HSN exceptions list.',
        );
        this.loading.set(false);
      },
    });
  }

  toggle(row: HsnExceptionRow): void {
    const next = new Set(this.expanded());
    if (next.has(row.hsnCode)) {
      next.delete(row.hsnCode);
      this.expanded.set(next);
      return;
    }
    next.add(row.hsnCode);
    this.expanded.set(next);

    // Only a sample ships with the report; fetch the rest on demand.
    if (row.productCount > row.sampleProducts.length) {
      this.hsnService.productsForCode(row.hsnCode).subscribe({
        next: (products) => {
          const current = this.report();
          if (!current) return;
          this.report.set({
            ...current,
            rows: current.rows.map((r) =>
              r.hsnCode === row.hsnCode ? { ...r, sampleProducts: products } : r,
            ),
          });
        },
      });
    }
  }

  isExpanded(row: HsnExceptionRow): boolean {
    return this.expanded().has(row.hsnCode);
  }

  isApplying(row: HsnExceptionRow): boolean {
    return this.applying().has(row.hsnCode);
  }

  /** Apply a corrected code to every product carrying the bad one. */
  apply(row: HsnExceptionRow, toCode: string): void {
    const code = (toCode ?? '').trim();
    if (!code) return;

    const busy = new Set(this.applying());
    busy.add(row.hsnCode);
    this.applying.set(busy);

    this.hsnService.applyCorrection(row.hsnCode, code).subscribe({
      next: (res) => {
        this.setRowMessage(
          row.hsnCode,
          `Updated ${res.updated} product${res.updated === 1 ? '' : 's'} to ${res.normalized}.`,
        );
        this.clearBusy(row.hsnCode);
        // Reload so the row disappears and the counts settle.
        this.load();
      },
      error: (e) => {
        this.setRowMessage(
          row.hsnCode,
          e?.error?.message ?? 'Could not apply that correction.',
        );
        this.clearBusy(row.hsnCode);
      },
    });
  }

  messageFor(row: HsnExceptionRow): string | undefined {
    return this.rowMessage()[row.hsnCode];
  }

  /** Portal lookup for a code we cannot resolve automatically. */
  portalUrl(code: string): string {
    return `https://services.gst.gov.in/services/searchhsnsac?inputText=${encodeURIComponent(code)}`;
  }

  private setRowMessage(code: string, message: string): void {
    this.rowMessage.set({ ...this.rowMessage(), [code]: message });
  }

  private clearBusy(code: string): void {
    const busy = new Set(this.applying());
    busy.delete(code);
    this.applying.set(busy);
  }
}
